const murmur3 = require("murmurhash-js/murmurhash3_gc");

/**
 * Memoizes a function; Instead of returning a cached result when passing the same argument references, it returns
 * a cached result when the last and new result share the same hash. The cache only saves the previous result per id.
 *
 * This memoize function is intended to use when the same function is called several times (e.g. distinct ids) during an store update
 * and calculating the result is cheap. The idea behind this is that we want to return the previous reference to avoid
 * React's reconciliation algorithm and rendering in Pure Components which are typically orders of magnitude more expensive.
 *
 * @param func The function to be memoize
 * @param replacer When we call JSON.Stringify on the result we may have a circular reference that needs to get stripped. Allow a function to strip those circular references
 */
export function memoizeLastResultPerId<
  T extends (id: string, ...args: any[]) => RET_TYPE,
  RET_TYPE
>(
  func: T,
  replacer?: ((this: any, key: string, value: any) => any) | undefined
): T {
  const dictionary: {
    [id: string]: { prevResult: RET_TYPE; resultHash: string };
  } = {};
  return function memoizedFunction(id: string, ...args: any[]): RET_TYPE {
    const result = func(id, ...args);
    // Use murmur3 to reduce the bytes needed to be stored in memory.

    const json = JSON.stringify(result, replacer);

    const resultHash = murmur3(json).toString(36);

    // return if we have a previous calculated result and they share the same hash
    if (dictionary[id] && dictionary[id].resultHash === resultHash) {
      return dictionary[id].prevResult;
    }
    dictionary[id] = { prevResult: result, resultHash };

    return result;
  } as T;
}
